import {
  AddNotificationCountToIndexedDB,
  AddReadNotificationToIndexedDB,
  AddUnreadNotificationToIndexedDB,
  ClearNotificationIndexedDB,
  GetNotificationCountFromIndexedDB,
  GetReadNotificationFromIndexedDB,
  GetUnreadNotificationFromIndexedDB,
  UpdateNotificationCountToIndexedDB,
  UpdateReadNotificationToIndexedDB,
  UpdateUnreadNotificationToIndexedDB,
} from "@/IndexedDB/notificationIndexedDB";
import { CheckIndexedDBIsSupport, ClearTabFromIndexedDB } from "@/IndexedDB/tabIndexedDB";
import { CheckBroadcastIsSupport } from "@/broadcastChannel/tabChannel";
import {
  BroadcastDisconnectNotificationAllTab,
  BroadcastIsUpdateIsSeenNotificationList,
  BroadcastIsUpdateNotificationCount,
  BroadcastIsUpdateUnSeenNotificationList,
  BroadcastMasterDeleteNotificationById,
  BroadcastMasterSetNotificationIsSeenAll,
  BroadcastMasterSetNotificationIsSeenById,
  BroadcastReCreateTab,
} from "@/broadcastChannel/userChannel";
import {
  FinanceType,
  NotificationEventType,
  NotificationMessageType,
  NotificationType,
  TransactionItemType,
  TransactionType,
} from "@/enums/enums";
import { i18n } from "@/i18n";
import { SelectedMerchantModel } from "@/model/merchant/selectedMerchantModel";
import { NotificationMessageForDisplayModel } from "@/model/notification/NotificationMessageForDisplayModel";
import { NotificationMessageModel } from "@/model/notification/NotificationMessageModel";
import { NotificationMessageOptionModel } from "@/model/notification/NotificationMessageOptionModel";
import { NotificationModel } from "@/model/notification/NotificationModel";
import { ObjectIdListNotification } from "@/model/notification/ObjectIdListNotificationModel";
import store from "@/store";
import Cookies from "js-cookie";
import Vue from "vue";
import { TranslateResult } from "vue-i18n";
import { GetCommentTextForDisplay, GetCookie, GetUserIdFromCommentText } from "./shareHelper";
import {
  ClearMasterTabFromCookies,
  ClearTabFromSessionStorage,
  GetAllTabs,
  GetTabIdFromSessionStorage,
  InsertTabIdToTabDB,
  IsMasterTab,
  IsMasterTabAvailable,
  SetMasterTab,
  UpdateTabMerchantId,
} from "./tabHelper";
import { AccessToken, UserId } from "./userHelper";

const appVue = new Vue();

const socketSessionIdKey = "socketPeakTaxSessionId";

export const GetSocketSessionIdFromCookies = () => {
  const socketSessionId = GetCookie(socketSessionIdKey);
  return socketSessionId ? socketSessionId : null;
};

export const SetSocketSessionIdFromCookies = () => {
  if (appVue.$socket && appVue.$socket.connected) {
    const socketSessionId = appVue.$socket.id;
    Cookies.set(socketSessionIdKey, socketSessionId);
  }
};

export const RemoveSocketSessionIdFromCookies = () => {
  Cookies.remove(socketSessionIdKey);
};

export const CheckStatusSocket = () => {
  if (IsMasterTab()) {
    if (!appVue.$socket || appVue.$socket.disconnected) {
      appVue.$socket.connect();
    }
  } else if (appVue.$socket && appVue.$socket.connected) {
    appVue.$socket.disconnect();
  }
};

export const SocketConnect = () => {
  if (IsMasterTab()) {
    if (appVue.$socket && appVue.$socket.disconnected) {
      appVue.$socket.connect();
    }
  } else if (!IsMasterTabAvailable()) {
    SetMasterTab();
    if (appVue.$socket && appVue.$socket.disconnected) {
      appVue.$socket.connect();
    }
    store.commit("Notification/MutationSetNotificationReadyToUse", true);
  } else {
    store.commit("Notification/MutationSetNotificationReadyToUse", true, { root: true });
  }
};

export const SocketAuthentication = () => {
  if (IsMasterTab() && appVue.$socket && appVue.$socket.connected) {
    const eui = btoa(UserId());
    const accessToken = AccessToken();
    appVue.$socket.emit("authentication", {
      eui: eui,
      accessToken: accessToken,
    });
  }
};

export const SocketDisconnect = () => {
  if (IsMasterTab() && appVue.$socket && appVue.$socket.connected) {
    appVue.$socket.disconnect();
  }
};

export const DoGetAllNotificationList = (merchantId: number) => {
  if (IsMasterTab() && appVue.$socket && appVue.$socket.connected) {
    store.dispatch("Notification/DoGetAllNotificationList", merchantId, { root: true });
  }
};

export const DoGetUnSeenNotificationList = (merchantId: number) => {
  if (IsMasterTab() && appVue.$socket && appVue.$socket.connected) {
    store.dispatch("Notification/DoGetUnSeenNotificationList", merchantId, { root: true });
  }
};

export const DoGetIsSeenNotificationList = (merchantId: number) => {
  if (IsMasterTab() && appVue.$socket && appVue.$socket.connected) {
    store.dispatch("Notification/DoGetIsSeenNotificationList", merchantId, { root: true });
  }
};

export const DoGetUnseenNotificationCount = (merchantId: number) => {
  if (IsMasterTab() && appVue.$socket && appVue.$socket.connected) {
    store.dispatch("Notification/DoGetUnseenNotificationCount", merchantId, { root: true });
  }
};

export const DoDeleteSeenNotification = (merchantId: number, notificationId: string) => {
  if (IsMasterTab() && appVue.$socket && appVue.$socket.connected) {
    store.dispatch(
      "Notification/DoDeleteSeenNotification",
      { merchantId: merchantId, notificationId: notificationId },
      { root: true }
    );
  }
};

export const DoDeleteUnseenNotification = (merchantId: number, notificationId: string) => {
  if (IsMasterTab() && appVue.$socket && appVue.$socket.connected) {
    store.dispatch(
      "Notification/DoDeleteUnseenNotification",
      { merchantId: merchantId, notificationId: notificationId },
      { root: true }
    );
  }
};

export const DoSetAllNotificationIsSeen = async (merchantId: number) => {
  if (IsMasterTab() && appVue.$socket && appVue.$socket.connected) {
    await store.dispatch("Notification/DoSetAllNotificationIsSeen", merchantId, { root: true });
  }
};

export const DoSetNotificationIsSeen = async (merchantId: number, notificationId: string) => {
  if (IsMasterTab() && appVue.$socket && appVue.$socket.connected) {
    await store.dispatch("Notification/DoSetNotificationIsSeen", {
      merchantId: merchantId,
      notificationId: notificationId,
    });
  }
};

export const DoAddNotificationCount = async (merchantId: number, notificationCount: number) => {
  const notificationCountModelForInsert = {
    merchantId: merchantId,
    notificationCount: notificationCount,
  };
  const notificationCountModel = await GetNotificationCountFromIndexedDB(merchantId);
  if (notificationCountModel) {
    await UpdateNotificationCountToIndexedDB(notificationCountModelForInsert);
  } else {
    await AddNotificationCountToIndexedDB(notificationCountModelForInsert);
  }
};

export const DoGetNotificationCount = async (merchantId: number) => {
  const notificationCountModel = await GetNotificationCountFromIndexedDB(merchantId);
  return notificationCountModel ? notificationCountModel.notificationCount : 0;
};

export const DoAddUnreadNotificationList = async (
  merchantId: number,
  notificationList: Array<NotificationMessageModel>
) => {
  const notificationModelForInsert: NotificationModel = {
    merchantId: merchantId,
    notificationList: notificationList,
  };

  const notificationModel = await GetUnreadNotificationFromIndexedDB(merchantId);

  if (notificationModel) {
    await UpdateUnreadNotificationToIndexedDB(notificationModelForInsert);
  } else {
    await AddUnreadNotificationToIndexedDB(notificationModelForInsert);
  }
};

export const DoGetReadAllNotificationList = async (merchantId: number) => {
  const notificationModel = await GetReadNotificationFromIndexedDB(merchantId);
  return notificationModel ? notificationModel.notificationList : [];
};

export const DoAddReadNotificationList = async (
  merchantId: number,
  notificationList: Array<NotificationMessageModel>
) => {
  const notificationModelForInsert = {
    merchantId: merchantId,
    notificationList: notificationList,
  };
  const notificationModel = await GetReadNotificationFromIndexedDB(merchantId);
  if (notificationModel) {
    await UpdateReadNotificationToIndexedDB(notificationModelForInsert);
  } else {
    await AddReadNotificationToIndexedDB(notificationModelForInsert);
  }
};

export const DoGetUnreadAllNotificationList = async (merchantId: number) => {
  const notificationModel = await GetUnreadNotificationFromIndexedDB(merchantId);
  return notificationModel ? notificationModel.notificationList : [];
};

export const RemoveNotificationFromNotificationList = (
  notificationList: Array<NotificationMessageModel>,
  notificationId: string
) => {
  if (notificationList && notificationList.length > 0) {
    const findIndex = notificationList.findIndex((x) => x.id === notificationId);
    if (findIndex > -1) {
      notificationList.splice(findIndex, 1);
      return notificationList;
    }
    return notificationList;
  }
  return [];
};

export const DoDeleteNotificationByIdFromSOCKET = async (merchantId: number, notificationId: string) => {
  if (IsMasterTab()) {
    let readNotificationList = await DoGetReadAllNotificationList(merchantId);
    readNotificationList = RemoveNotificationFromNotificationList(readNotificationList, notificationId);
    await UpdateReadNotificationToIndexedDB({ merchantId: merchantId, notificationList: readNotificationList });

    let unreadNotificationList = await DoGetUnreadAllNotificationList(merchantId);
    unreadNotificationList = RemoveNotificationFromNotificationList(unreadNotificationList, notificationId);
    await UpdateUnreadNotificationToIndexedDB({ merchantId: merchantId, notificationList: unreadNotificationList });
    const notificationCount = await DoGetNotificationCount(merchantId);
    await UpdateNotificationCountToIndexedDB({ merchantId: merchantId, notificationCount: notificationCount - 1 });

    await store.dispatch("Notification/ActionSetNotificationCountFromIndexedDB", null, { root: true });
    BroadcastIsUpdateNotificationCount(merchantId);
    await store.dispatch("Notification/ActionSetUnSeenNotificationListFromIndexedDB", null, { root: true });
    BroadcastIsUpdateUnSeenNotificationList(merchantId);
    await store.dispatch("Notification/ActionSetIsSeenNotificationListFromIndexedDB", null, { root: true });
    BroadcastIsUpdateIsSeenNotificationList(merchantId);
  }
};

export const DoDeleteNotificationById = async (merchantId: number, notificationId: string, isSeen: boolean) => {
  if (IsMasterTab()) {
    if (isSeen) {
      DoDeleteSeenNotification(merchantId, notificationId);
    } else {
      DoDeleteUnseenNotification(merchantId, notificationId);
    }
    await DoDeleteNotificationByIdFromSOCKET(merchantId, notificationId);
  } else {
    BroadcastMasterDeleteNotificationById(merchantId, notificationId, isSeen);
  }
};

export const DoSetNotificationIsSeenAllFromSOCKET = async (merchantId: number) => {
  if (IsMasterTab()) {
    await UpdateUnreadNotificationToIndexedDB({ merchantId: merchantId, notificationList: [] });
    await UpdateNotificationCountToIndexedDB({ merchantId: merchantId, notificationCount: 0 });

    const unreadNotificationList = await DoGetUnreadAllNotificationList(merchantId);
    const readNotificationList = await DoGetReadAllNotificationList(merchantId);
    if (unreadNotificationList && unreadNotificationList.length > 0) {
      await DoAddReadNotificationList(merchantId, [...unreadNotificationList, ...readNotificationList]);
    } else if (readNotificationList && readNotificationList.length > 0) {
      DoGetIsSeenNotificationList(merchantId);
    }

    await store.dispatch("Notification/ActionSetNotificationCountFromIndexedDB", null, { root: true });
    BroadcastIsUpdateNotificationCount(merchantId);
    await store.dispatch("Notification/ActionSetUnSeenNotificationListFromIndexedDB", null, { root: true });
    BroadcastIsUpdateUnSeenNotificationList(merchantId);
    await store.dispatch("Notification/ActionSetIsSeenNotificationListFromIndexedDB", null, { root: true });
    BroadcastIsUpdateIsSeenNotificationList(merchantId);
  }
};

export const DoSetNotificationIsSeenAll = async (merchantId: number) => {
  if (IsMasterTab()) {
    await DoSetAllNotificationIsSeen(merchantId);
    const unreadNotificationList = await DoGetUnreadAllNotificationList(merchantId);
    const readNotificationList = await DoGetReadAllNotificationList(merchantId);
    await UpdateUnreadNotificationToIndexedDB({ merchantId: merchantId, notificationList: [] });
    await UpdateNotificationCountToIndexedDB({ merchantId: merchantId, notificationCount: 0 });

    await DoAddReadNotificationList(merchantId, [...unreadNotificationList, ...readNotificationList]);

    await store.dispatch("Notification/ActionSetNotificationCountFromIndexedDB", null, { root: true });
    BroadcastIsUpdateNotificationCount(merchantId);
    await store.dispatch("Notification/ActionSetUnSeenNotificationListFromIndexedDB", null, { root: true });
    BroadcastIsUpdateUnSeenNotificationList(merchantId);
    await store.dispatch("Notification/ActionSetIsSeenNotificationListFromIndexedDB", null, { root: true });
    BroadcastIsUpdateIsSeenNotificationList(merchantId);
  } else {
    BroadcastMasterSetNotificationIsSeenAll(merchantId);
  }
};

export const DoSetNotificationIsSeenByIdFromSOCKET = async (merchantId: number, notificationId: string) => {
  if (IsMasterTab()) {
    const unreadNotificationCount = await DoGetNotificationCount(merchantId);

    await UpdateNotificationCountToIndexedDB({
      merchantId: merchantId,
      notificationCount: unreadNotificationCount - 1,
    });

    const unreadNotificationList = await DoGetUnreadAllNotificationList(merchantId);
    const readNotificationList = await DoGetReadAllNotificationList(merchantId);

    if (unreadNotificationList && unreadNotificationList.length > 0) {
      const unreadNotification = unreadNotificationList.find((x) => x.id == notificationId);

      if (unreadNotification) {
        unreadNotification.isSeen = true;
        await DoAddReadNotificationList(merchantId, [...readNotificationList, unreadNotification]);
      }

      await UpdateUnreadNotificationToIndexedDB({
        merchantId: merchantId,
        notificationList: unreadNotificationList.filter((x) => x.id !== notificationId),
      });
    } else if (readNotificationList && readNotificationList.length > 0) {
      DoGetIsSeenNotificationList(merchantId);
    }

    await store.dispatch("Notification/ActionSetNotificationCountFromIndexedDB", null, { root: true });
    BroadcastIsUpdateNotificationCount(merchantId);
    await store.dispatch("Notification/ActionSetUnSeenNotificationListFromIndexedDB", null, { root: true });
    BroadcastIsUpdateUnSeenNotificationList(merchantId);
    await store.dispatch("Notification/ActionSetIsSeenNotificationListFromIndexedDB", null, { root: true });
    BroadcastIsUpdateIsSeenNotificationList(merchantId);
  }
};

export const DoSetNotificationIsSeenById = async (merchantId: number, notificationId: string) => {
  if (IsMasterTab()) {
    await DoSetNotificationIsSeen(merchantId, notificationId);
    const unreadNotificationList = await DoGetUnreadAllNotificationList(merchantId);
    const readNotificationList = await DoGetReadAllNotificationList(merchantId);
    const unreadNotificationCount = await DoGetNotificationCount(merchantId);

    const unreadNotification = unreadNotificationList.find((x) => x.id == notificationId);
    if (unreadNotification) {
      await UpdateNotificationCountToIndexedDB({
        merchantId: merchantId,
        notificationCount: unreadNotificationCount - 1,
      });

      unreadNotification.isSeen = true;
      await DoAddReadNotificationList(merchantId, [...readNotificationList, unreadNotification]);
    }

    await UpdateUnreadNotificationToIndexedDB({
      merchantId: merchantId,
      notificationList: unreadNotificationList.filter((x) => x.id !== notificationId),
    });

    await store.dispatch("Notification/ActionSetNotificationCountFromIndexedDB", null, { root: true });
    BroadcastIsUpdateNotificationCount(merchantId);
    await store.dispatch("Notification/ActionSetUnSeenNotificationListFromIndexedDB", null, { root: true });
    BroadcastIsUpdateUnSeenNotificationList(merchantId);
    await store.dispatch("Notification/ActionSetIsSeenNotificationListFromIndexedDB", null, { root: true });
    BroadcastIsUpdateIsSeenNotificationList(merchantId);
  } else {
    BroadcastMasterSetNotificationIsSeenById(merchantId, notificationId);
  }
};

export const SetIsSeenForNotification = (notificationList: Array<NotificationMessageModel>, isSeen: boolean) => {
  if (notificationList && notificationList.length > 0) {
    notificationList.forEach((notificationItem) => {
      notificationItem.isSeen = isSeen;
    });
    return notificationList;
  }
  return [];
};

export const SortDataNotification = (notificationList: Array<NotificationMessageForDisplayModel>) => {
  if (notificationList && notificationList.length > 0) {
    return notificationList.sort((a, b) => {
      const notificationDateTimeA = new Date(a.notificationDateTime);
      const notificationDateTimeB = new Date(b.notificationDateTime);
      return notificationDateTimeB.getTime() - notificationDateTimeA.getTime();
    });
  }
  return [];
};

export const GetObjectIdListNotification = (notificationList: Array<NotificationMessageForDisplayModel>) => {
  const userIdList = [];
  const quotationIdList = [];
  const depositIdList = [];
  const goodsReceiptIdList = [];
  const invoiceReceiptIdList = [];
  const invoiceIdList = [];
  const receiptIdList = [];
  const taxInvoiceIdList = [];
  const creditNoteIdList = [];
  const debitNoteIdList = [];
  const billingNoteIdList = [];
  const journalIdList = [];
  const pnd2IdList = [];
  const pnd3IdList = [];
  const pnd53IdList = [];
  const pp30IdList = [];
  const productIdList = [];
  const assetTemplateIdList = [];
  const purchaseRequisitionIdList = [];
  const productRequisitionIdList = [];
  const bankAccountIdList = [];

  if (notificationList && notificationList.length > 0) {
    for (const notification of notificationList) {
      if (notification.userId > 0) {
        userIdList.push(notification.userId);
      }
      if (notification.option && notification.option.referenceUserId > 0) {
        userIdList.push(notification.option.referenceUserId);
      }
      if (notification.messageTypeId == NotificationMessageType.MentionTransaction) {
        const userIds = GetUserIdFromCommentText(notification.option.messageDescription);
        userIdList.push(...userIds);
      }

      if (notification.option.productId > 0) {
        productIdList.push(notification.option.productId);
      }

      if (notification.option.assetTemplateId > 0) {
        assetTemplateIdList.push(notification.option.assetTemplateId);
      }

      if (notification.option.transactionType == TransactionType.Pnd2) {
        pnd2IdList.push(notification.option.transactionId);
      }

      if (notification.option.transactionType == TransactionType.TaxPND3) {
        pnd3IdList.push(notification.option.transactionId);
      }

      if (notification.option.transactionType == TransactionType.TaxPND53) {
        pnd53IdList.push(notification.option.transactionId);
      }

      if (notification.option.transactionType == TransactionType.TaxPP30) {
        pp30IdList.push(notification.option.transactionId);
      }

      if (notification.option.transactionType == TransactionType.ProductRequisition) {
        productRequisitionIdList.push(notification.option.transactionId);
      }

      if (notification.option.transactionType == TransactionType.PurchaseRequisition) {
        purchaseRequisitionIdList.push(notification.option.transactionId);
      }

      if (notification.option.transactionType == TransactionType.Quotation) {
        quotationIdList.push(notification.option.transactionId);
      }

      if (notification.option.transactionType == TransactionType.Deposit) {
        depositIdList.push(notification.option.transactionId);
      }

      if (notification.option.transactionType == TransactionType.GoodsReceipt) {
        goodsReceiptIdList.push(notification.option.transactionId);
      }

      if (notification.option.transactionType == TransactionType.InvoiceReceipt) {
        invoiceReceiptIdList.push(notification.option.transactionId);
      }

      if (notification.option.transactionType == TransactionType.Invoice) {
        invoiceIdList.push(notification.option.transactionId);
      }

      if (notification.option.transactionType == TransactionType.Receipt) {
        receiptIdList.push(notification.option.transactionId);
      }

      if (notification.option.transactionType == TransactionType.TaxInvoice) {
        taxInvoiceIdList.push(notification.option.transactionId);
      }

      if (notification.option.transactionType == TransactionType.CreditNote) {
        creditNoteIdList.push(notification.option.transactionId);
      }

      if (notification.option.transactionType == TransactionType.DebitNote) {
        debitNoteIdList.push(notification.option.transactionId);
      }

      if (notification.option.transactionType == TransactionType.BillingNote) {
        billingNoteIdList.push(notification.option.transactionId);
      }

      if (notification.option.transactionType == TransactionType.DailyJournal) {
        journalIdList.push(notification.option.transactionId);
      }
      if (notification.option.bankAccountId > 0) {
        bankAccountIdList.push(notification.option.bankAccountId);
      }
    }
  }
  const objectIdListNotification: ObjectIdListNotification = {
    userIdList,
    productRequisitionIdList,
    purchaseRequisitionIdList,
    quotationIdList,
    depositIdList,
    goodsReceiptIdList,
    invoiceReceiptIdList,
    invoiceIdList,
    receiptIdList,
    taxInvoiceIdList,
    creditNoteIdList,
    debitNoteIdList,
    billingNoteIdList,
    journalIdList,
    pnd2IdList,
    pnd3IdList,
    pnd53IdList,
    pp30IdList,
    productIdList,
    assetTemplateIdList,
    bankAccountIdList,
  };
  return objectIdListNotification;
};

export const GetUserImageByUserId = (userId: number, userIdListForMap: Map<number, any>) => {
  let userImage = "";
  if (userIdListForMap && userIdListForMap.get(userId)) {
    userImage = userIdListForMap.get(userId).pictureUrl;
  }
  return userImage;
};

export const GetProductCodeByProductId = (productId: number, productCodeListForMap: Map<number, any>) => {
  const productCode = productCodeListForMap.get(productId);
  if (productCode) {
    return productCode.productTemplateCode;
  }
  return "";
};

export const GetAssetTemplateCodeByAssetTemplateId = (
  assetTemplateId: number,
  assetTemplateCodeListForMap: Map<number, any>
) => {
  const assetTemplateCode = assetTemplateCodeListForMap.get(assetTemplateId);
  if (assetTemplateCode) {
    return assetTemplateCode.assetTemplateCode;
  }
  return "";
};
export const GetBankAccountCodeByBankAccountId = (bankAccountId: number, bankAccountListForMap: Map<number, any>) => {
  const bankAccountCode = bankAccountListForMap.get(bankAccountId);
  if (bankAccountCode) {
    return bankAccountCode.code;
  }
  return "";
};
export const GetTransactionNumberFromTransactionTypeListForMap = (
  transactionId: number,
  transactionType: TransactionType,
  transactionTypeListForMap: Map<number, any>,
  journalViewDetailListForMap: Map<number, any>
) => {
  if (transactionType === TransactionType.DailyJournal) {
    const journalViewDetail = journalViewDetailListForMap.get(transactionId);
    if (journalViewDetail) {
      return journalViewDetail.number;
    }
  } else {
    const transactionNumberForMap = transactionTypeListForMap.get(transactionType);
    if (transactionNumberForMap) {
      const transactionNumber = transactionNumberForMap.get(transactionId);
      if (transactionNumber) {
        return transactionNumber.number;
      }
    }
  }
  return "";
};

export const CheckMerchantIsActiveOtherTab = async (merchantId: number) => {
  const tabId = GetTabIdFromSessionStorage();
  if (tabId) {
    const allTabs = await GetAllTabs();
    return allTabs.some((x) => x.id !== tabId && x.merchantId == merchantId);
  }
  return false;
};

export const CheckMerchantIsLoadNotificationUnSeen = async (merchantId: number) => {
  const unreadNotificationList = await DoGetUnreadAllNotificationList(merchantId);
  return unreadNotificationList && unreadNotificationList.length ? true : false;
};

export const CheckMerchantIsLoadNotificationIsSeen = async (merchantId: number) => {
  const readNotificationList = await DoGetReadAllNotificationList(merchantId);
  return readNotificationList && readNotificationList.length ? true : false;
};

export const CheckMerchantIsLoadAllNotification = async (merchantId: number) => {
  const unreadNotificationList = await DoGetUnreadAllNotificationList(merchantId);
  const readNotificationList = await DoGetReadAllNotificationList(merchantId);
  return unreadNotificationList && unreadNotificationList.length && readNotificationList && readNotificationList.length
    ? true
    : false;
};

export const CheckMerchantIsActive = async (merchantId: number) => {
  let isActive = false;
  if (merchantId > 0) {
    if (CheckBroadcastIsSupport() && CheckIndexedDBIsSupport()) {
      const allTabs = await GetAllTabs();
      const isHaveSomeTabSameMerchant = allTabs.some((x) => x.merchantId == merchantId);
      if (isHaveSomeTabSameMerchant) {
        isActive = true;
      }
    } else {
      isActive = true;
    }
  }
  return isActive;
};

export const ForceSetMaster = async (merchantId: number) => {
  BroadcastDisconnectNotificationAllTab();
  ClearMasterTabFromCookies();
  ClearTabFromSessionStorage();
  await ClearTabFromIndexedDB();
  await ClearNotificationIndexedDB();
  await InsertTabIdToTabDB();
  await UpdateTabMerchantId(merchantId);
  DoGetUnseenNotificationCount(merchantId);
  BroadcastReCreateTab();
};

export const GetTransactionNameForNotification = (
  transactionType: TransactionType,
  financeType: FinanceType,
  transactionItemType: TransactionItemType = TransactionItemType.None
) => {
  let name: any = "";
  switch (transactionType) {
    case TransactionType.Quotation:
      if (financeType == FinanceType.InFlow) {
        name = i18n.t("Quotation");
      } else {
        if (transactionItemType == TransactionItemType.Asset) {
          name = i18n.t("Purchase_Order_Asset");
        } else {
          name = i18n.t("Purchase_Order");
        }
      }
      break;
    case TransactionType.Deposit:
      if (financeType == FinanceType.InFlow) {
        name = i18n.t("Deposit_Receipt");
      } else {
        name = i18n.t("Deposit_Payment");
      }
      break;
    case TransactionType.GoodsReceipt:
      name = i18n.t("Goods_Receipt");
      break;
    case TransactionType.InvoiceReceipt:
      name = i18n.t("Invoice_Receipt");
      break;
    case TransactionType.Invoice:
      if (financeType == FinanceType.InFlow) {
        name = i18n.t("Invoice");
      } else {
        if (transactionItemType == TransactionItemType.Asset) {
          name = i18n.t("Purchase_New_Asset");
        } else {
          name = i18n.t("Expense");
        }
      }
      break;
    case TransactionType.Receipt:
      name = i18n.t("Receipt");
      break;
    case TransactionType.TaxInvoice:
      if (financeType == FinanceType.InFlow) {
        name = i18n.t("Sales_Tax_Invoice");
      } else {
        name = i18n.t("Purchase_Tax_Invoice");
      }

      break;
    case TransactionType.CreditNote:
      if (financeType == FinanceType.InFlow) {
        name = i18n.t("Credit_Note");
      } else {
        name = i18n.t("Receive_Credit_Note");
      }

      break;
    case TransactionType.DebitNote:
      if (financeType == FinanceType.InFlow) {
        name = i18n.t("Debit_Note");
      } else {
        name = i18n.t("Receive_Debit_Note");
      }
      break;
    case TransactionType.BillingNote:
      if (financeType == FinanceType.InFlow) {
        name = i18n.t("Menu_BillingNote");
      } else {
        name = i18n.t("Combined_Payment_Note");
      }
      break;
    case TransactionType.DailyJournal:
      name = i18n.t("Daily_Journal");
      break;
    case TransactionType.WithholdingTax:
      name = i18n.t("Withholding_Tax_Certificate");
      break;
    case TransactionType.PurchaseRequisition:
      name = i18n.t("Menu_PurchaseRequisition");
      break;
    case TransactionType.ProductRequisition:
      name = i18n.t("Product_Requisition");
      break;
  }

  return name;
};
export const GetTimeFormat = (notificationDateTime: Date) => {
  const date = new Date(notificationDateTime);
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};
export const GetRoleName = (messageDescription: string) => {
  try {
    if (!messageDescription || typeof messageDescription !== "string") {
      return "";
    }
    const role = JSON.parse(messageDescription);
    const { roleName } = role;
    return roleName ? roleName[i18n.locale] : "";
  } catch (error) {
    return messageDescription || "";
  }
};
function getExcelReportRowLimitExceedNotificationDescription(
  exportFromDate: string | undefined,
  exportToDate: string | undefined,
  transactionCount: string | undefined
) {
  const EXCEL_REPORT_MAXIMUM_TRANSACTION_COUNT_LIMIT = 100000;
  const periodDescription = i18n.t("Export_Period_Description", {
    fromDate: exportFromDate,
    toDate: exportToDate,
  });
  const rowLimitIs100000 = i18n.t("Row_Limit_Is_100000_But_The_Selected_Period_Has_NumberRow", {
    limit: EXCEL_REPORT_MAXIMUM_TRANSACTION_COUNT_LIMIT.toString().FormatMoney(0),
    numberRow: transactionCount?.toString().FormatMoney(0),
  });
  return `${periodDescription}<br>${rowLimitIs100000}`;
}
function getUnexpectedErrorWhileCreatingReportNotificationDescription(
  exportFromDate: string | undefined,
  exportToDate: string | undefined
) {
  const periodDescription = i18n.t("Export_Period_Description", {
    fromDate: exportFromDate,
    toDate: exportToDate,
  });
  const dueToASystemErrorWhileGeneratingReport = i18n.t("Due_To_A_System_Error_While_Generating_The_Report");
  return `${periodDescription}<br>${dueToASystemErrorWhileGeneratingReport}`;
}
function getReportHasBeenSentToEmailNotificationDescription(
  exportFromDate: string | undefined,
  exportToDate: string | undefined,
  receiveEmailList: string[] | undefined
) {
  const descriptionMessage =
    i18n.t("Export_Period_Description", {
      fromDate: exportFromDate,
      toDate: exportToDate,
    }) +
    `<br>` +
    i18n.t("Report_Has_Been_Sent_To_Email_Description", {
      commaSeparatedEmailAddresses: receiveEmailList ?? [],
    });
  return descriptionMessage;
}
function getExportPeriodDescription(
  exportFromDate: string | undefined,
  exportToDate: string | undefined
): TranslateResult {
  const result = i18n.t("Export_Period_Description", {
    fromDate: exportFromDate,
    toDate: exportToDate,
  });
  return result;
}
interface NotificationHeaderAndDescriptionMessageParameter {
  reportName: TranslateResult | string;
  successMessage: TranslateResult | string;
  notificationEventType: number;
  transactionCount?: string;
  exportFromDate?: string;
  exportToDate?: string;
  receiveEmailList?: string[];
  descriptionMessageOnSuccess: TranslateResult;
}
function getNotificationHeaderAndDescriptionMessage(parameter: NotificationHeaderAndDescriptionMessageParameter): {
  headerMessage: TranslateResult;
  descriptionMessage: TranslateResult;
} {
  let headerMessage: TranslateResult = "";
  let descriptionMessage: TranslateResult = "";
  const reportName = parameter.reportName;
  const successMessage = parameter.successMessage;
  const exportFromDate = parameter.exportFromDate;
  const exportToDate = parameter.exportToDate;
  const transactionCount = parameter.transactionCount;
  const receiveEmailList = parameter.receiveEmailList;
  const descriptionMessageOnSuccess = parameter.descriptionMessageOnSuccess;
  switch (parameter.notificationEventType) {
    case NotificationEventType.ExcelReportRowLimitExceed:
      headerMessage = i18n.t("Export_Report_Unsuccessful", { reportName: reportName });
      descriptionMessage = getExcelReportRowLimitExceedNotificationDescription(
        exportFromDate,
        exportToDate,
        transactionCount
      );
      break;
    case NotificationEventType.UnexpectedErrorWhileCreatingReport:
      headerMessage = i18n.t("Export_Report_Unsuccessful", { reportName: reportName });
      descriptionMessage = getUnexpectedErrorWhileCreatingReportNotificationDescription(exportFromDate, exportToDate);
      break;
    case NotificationEventType.ReportHasBeenSentToEmail:
      headerMessage = i18n.t("Report_Has_Been_Sent_To_Email", { reportName: reportName });
      descriptionMessage = getReportHasBeenSentToEmailNotificationDescription(
        exportFromDate,
        exportToDate,
        receiveEmailList
      );
      break;
    default:
      headerMessage = successMessage;
      descriptionMessage = descriptionMessageOnSuccess;
      break;
  }
  return { headerMessage, descriptionMessage };
}
export const GetSuccessHeaderReport = (
  messageTypeId: number,
  option: NotificationMessageOptionModel,
  notificationDateTime: Date,
  merchant: SelectedMerchantModel,
  userIdListForMap: Map<number, any>,
  transactionTypeListForMap: Map<number, any>,
  journalViewDetailListForMap: Map<number, any>,
  productCodeListForMap: Map<number, any>,
  assetTemplateCodeListForMap: Map<number, any>,
  bankAccountListForMap: Map<number, any>
) => {
  let headerMessage: any = "";
  let descriptionMessage: any = "";

  const merchantName = merchant ? merchant.merchantName : "";
  const messageDescription = option.messageDescription ? option.messageDescription : "";
  const remark = option.remark ? option.remark : "";
  const roleName = GetRoleName(messageDescription);
  const exportFromDate = option.exportFromDate ? option.exportFromDate.GetDateFormat() : "";
  const exportToDate = option.exportToDate ? option.exportToDate.GetDateFormat() : "";
  const transactionCount = option.transactionCount ? option.transactionCount.toString().FormatMoney(0) : "0";
  const transactionId = option.transactionId ? option.transactionId : 0;
  const transactionType = option.transactionType ? option.transactionType : 0;
  const financeType = option.financeType ? option.financeType : 0;
  const transactionItemType = option.transactionItemType ? option.transactionItemType : 0;
  const productId = option.productId ? option.productId : 0;
  const assetTemplateId = option.assetTemplateId ? option.assetTemplateId : 0;
  const bankAccountId = option.bankAccountId ? option.bankAccountId : 0;
  const userName = userIdListForMap.get(option.referenceUserId)
    ? userIdListForMap.get(option.referenceUserId).displayName
    : "";
  const messageUrlName = option.messageUrlName ? option.messageUrlName : "";
  const notificationTime = GetTimeFormat(notificationDateTime);
  const receiveEmailList = option.receiveEmailList;
  const notificationEventType = option.notificationEventType ? option.notificationEventType : 0;

  switch (messageTypeId) {
    case NotificationMessageType.Quotation:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("QuotationReport"),
        successMessage: i18n.t("Success_QuotationReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.PurcahseOrder:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("POReport"),
        successMessage: i18n.t("Success_POReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.PurchaseOrderAsset:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("POAssetReport"),
        successMessage: i18n.t("Success_POAssetReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.PurchaseRequisition:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("PurchaseRequisition"),
        successMessage: i18n.t("Success_PurchaseRequisition"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.ProductRequisition:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("PurchaseRequisition"),
        successMessage: i18n.t("Success_PurchaseRequisition"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.Invoice:
    case NotificationMessageType.InvoiceAllProduct:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("InvoiceReport"),
        successMessage: i18n.t("Success_InvoiceReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.Expense:
    case NotificationMessageType.ExpenseAllProduct:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("ExpenseReport"),
        successMessage: i18n.t("Success_ExpenseReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.PurchaseAsset:
    case NotificationMessageType.PurchaseAssetAllProduct:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("PurchaseAssetReport"),
        successMessage: i18n.t("Success_PurchaseAssetReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.Receipt:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("ReceiptReport"),
        successMessage: i18n.t("Success_ReceiptReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.TaxInvoice:
    case NotificationMessageType.TaxInvoiceInRevenueDepartmentFormat:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("TaxInvoiceReport"),
        successMessage: i18n.t("Success_TaxInvoiceReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.PurcahseTaxInvoice:
    case NotificationMessageType.PurchaseTaxInvoiceInRevenueDepartFormat:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("PurchaseTaxInvoiceReport"),
        successMessage: i18n.t("Success_PurchaseTaxInvoiceReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.AllReportTaxInvoiceFromOldPeak:
      headerMessage = i18n.t("Success_TaxInvoiceReportFromOldPeak");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.AllReportPurchaseTaxInvoiceFromOldPeak:
      headerMessage = i18n.t("Success_PurchaseTaxInvoiceReportFromOldPeak");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.AllReportTaxInvoiceFromPeakTax:
      headerMessage = i18n.t("Success_TaxInvoiceReportFromPeakTax");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.AllReportPurchaseTaxInvoiceFromPeakTax:
      headerMessage = i18n.t("Success_PurchaseTaxInvoiceReportFromPeakTax");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.BillingNoteInvoice:
    case NotificationMessageType.BillingNoteInvoiceAllInvoice:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("BillingNoteReport"),
        successMessage: i18n.t("Success_BillingNoteReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.BillingNoteExpense:
    case NotificationMessageType.BillingNoteExpenseAllExpense:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("CombinePaymentNoteReport"),
        successMessage: i18n.t("Success_CombinePaymentNoteReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.StockActivityStockCardMovement:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("StockMovementReport"),
        successMessage: i18n.t("Success_StockMovementReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.Journal:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("DailyJournalReport"),
        successMessage: i18n.t("Success_DailyJournalReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: i18n.t("Export_Period_Description", {
          fromDate: exportFromDate,
          toDate: exportToDate,
        }),
      }));
      break;
    case NotificationMessageType.TrialBalance:
    case NotificationMessageType.TrialBalanceWithSubAccount:
      headerMessage = i18n.t("Success_TrialBalanceReport");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.TrialBalanceSeperateMonth:
      headerMessage = i18n.t("Success_TrialBalanceSeperateMonthReport");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.Finance:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("FinanceReport"),
        successMessage: i18n.t("Success_FinanceReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.PP30InputVat:
      headerMessage = i18n.t("Success_PP30InputVat");
      break;
    case NotificationMessageType.PP30OutputVat:
      headerMessage = i18n.t("Success_PP30OutputVat");
      break;
    case NotificationMessageType.CreditNote:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("CreditnoteReport"),
        successMessage: i18n.t("Success_CreditnoteReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.CreditNoteExpense:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("CreditnoteExpenseReport"),
        successMessage: i18n.t("Success_CreditnoteExpenseReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.AssetTemplate:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("AssetTemplateReportReport"),
        successMessage: i18n.t("Success_AssetTemplateReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.AssetTemplateGroup:
      headerMessage = i18n.t("Success_AssetTemplateGroupReport");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.WithHeldTax:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("WithHeldTaxReport"),
        successMessage: i18n.t("Success_WithHeldTaxReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.WithHoldingTax:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("WithHoldingTaxReport"),
        successMessage: i18n.t("Success_WithHoldingTaxReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.GeneralLedgerTransaction:
      headerMessage = i18n.t("Success_GeneralLedger_Transaction");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.AllGeneralLedger:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("AllGeneralLedger"),
        successMessage: i18n.t("Success_AllGeneralLedger"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.AllGeneralLedgerWithSubAccount:
      headerMessage = i18n.t("Success_GeneralLedger_Transaction");
      break;
    case NotificationMessageType.AgingReportAR:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("Aging_Report_AR"),
        successMessage: i18n.t("Success_Aging_Report_AR"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.AgingReportAP:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("Aging_Report_AP"),
        successMessage: i18n.t("Success_Aging_Report_AP"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.Product:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("ProductReport"),
        successMessage: i18n.t("Success_Product"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.ProfitLoss:
      headerMessage = i18n.t("Success_ProfitLoss");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.ChartOfAccount:
      headerMessage = i18n.t("Success_ChartOfAccount");
      descriptionMessage = i18n.t("Success_ChartOfAccount_Description", {
        merchantName: merchantName,
      });
      break;
    case NotificationMessageType.BalanceSheet:
      headerMessage = i18n.t("Success_BalanceSheet");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.Unit:
      headerMessage = i18n.t("Success_Unit");
      break;
    case NotificationMessageType.ReportDocument:
      headerMessage = i18n.t("Success_ReportDocument");
      break;
    case NotificationMessageType.Asset:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("AssetReport"),
        successMessage: i18n.t("Success_AssetReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: i18n.t("Success_AssetReport_Description", {
          fromDate: exportFromDate,
          toDate: exportToDate,
          assetGroupCode: GetAssetTemplateCodeByAssetTemplateId(assetTemplateId, assetTemplateCodeListForMap),
        }),
      }));
      break;
    case NotificationMessageType.StatementEFiling:
      headerMessage = i18n.t("Success_StatementEFiling");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.Contact:
      headerMessage = i18n.t("Success_ReportContact");
      break;
    case NotificationMessageType.PurchaseTaxInvoiceNotUse:
      headerMessage = i18n.t("AllReportPurchaseTaxInvoiceNotUse");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.SalesTaxInvoiceNotUse:
      headerMessage = i18n.t("AllReportSalesTaxInvoiceNotUse");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.AllReportPurchaseTaxInvoiceNotUseFromOldPeak:
      headerMessage = i18n.t("AllReportPurchaseTaxInvoiceNotUseFromOldPeak");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.AllReportSalesTaxInvoiceNotUseFromOldPeak:
      headerMessage = i18n.t("AllReportSalesTaxInvoiceNotUseFromOldPeak");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.AllReportPurchaseTaxInvoiceNotUseFromPeakTax:
      headerMessage = i18n.t("AllReportPurchaseTaxInvoiceNotUseFromPeakTax");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.AllReportSalesTaxInvoiceNotUseFromPeakTax:
      headerMessage = i18n.t("AllReportSalesTaxInvoiceNotUseFromPeakTax");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.PP30TaxFilingTextFile:
      headerMessage = i18n.t("Success_PP30TaxFilingTextFile");
      break;
    case NotificationMessageType.WithHoldingTaxPND2:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("WithHoldingTaxPND2Report"),
        successMessage: i18n.t("Success_WithHoldingTaxPND2"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: "",
      }));
      break;
    case NotificationMessageType.WithHoldingTaxPND3:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("WithHoldingTaxPND3Report"),
        successMessage: i18n.t("Success_WithHoldingTaxPND3"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: "",
      }));
      break;
    case NotificationMessageType.WithHoldingTaxPND53:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("WithHoldingTaxPND53Report"),
        successMessage: i18n.t("Success_WithHoldingTaxPND53"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: "",
      }));
      break;
    case NotificationMessageType.CashflowStatementReport:
      headerMessage = i18n.t("Success_CashflowStatementReport");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.AllReportSalesByCustomerReport:
      headerMessage = i18n.t("Success_AllReportSalesByCustomerReport");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.AllReportSalesByAccountReport:
      headerMessage = i18n.t("Success_AllReportSalesByAccountReport");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.StatementEFilingExcel:
      headerMessage = i18n.t("Success_StatementEFilingExcel");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.AllReportProductSalesByContact:
      headerMessage = i18n.t("Success_AllReportProductSalesByContact");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.AllReport_ProductServiceSalesSummaryReportOnDailyAccount:
      headerMessage = i18n.t("Success_AllReport_ProductServiceSalesSummaryReportOnDailyAccount");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.AllReport_RemainingFinanceBalanceReport:
      headerMessage = i18n.t("Success_AllReport_RemainingFinanceBalanceReport");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.Pnd53TaxFilingTextFile:
      headerMessage = i18n.t("Success_Pnd53TaxFilingTextFile");
      break;
    case NotificationMessageType.Pnd3TaxFilingTextFile:
      headerMessage = i18n.t("Success_Pnd3TaxFilingTextFile");
      break;
    case NotificationMessageType.DailyJournal:
      headerMessage = i18n.t("Success_DailyJournal");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.DailyJournal, FinanceType.None),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DocumentQuotation:
      headerMessage = i18n.t("Success_DocumentQuotation");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.Quotation, FinanceType.InFlow),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DocumentInvoice:
      headerMessage = i18n.t("Success_DocumentInvoice");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.Invoice, FinanceType.InFlow),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DocumentReceipt:
      headerMessage = i18n.t("Success_DocumentReceipt");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.Receipt, FinanceType.InFlow),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DocumentTaxInvoice:
      headerMessage = i18n.t("Success_DocumentTaxInvoice");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.TaxInvoice, FinanceType.InFlow),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DocumentPurchaseOrder:
      headerMessage = i18n.t("Success_DocumentPurchaseOrder");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.Quotation, FinanceType.OutFlow),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DocumentExpense:
      headerMessage = i18n.t("Success_DocumentExpense");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.Invoice, FinanceType.OutFlow),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DocumentBillingNote:
      headerMessage = i18n.t("Success_DocumentBillingNote");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.BillingNote, FinanceType.InFlow),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DocumentBillingNoteExpense:
      headerMessage = i18n.t("Success_DocumentBillingNoteExpress");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.BillingNote, FinanceType.OutFlow),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DocumentCreditNote:
      headerMessage = i18n.t("Success_DocumentCreditNote");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.CreditNote, FinanceType.InFlow),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DocumentCreditNoteExpense:
      headerMessage = i18n.t("Success_DocumentCreditNoteExpress");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.CreditNote, FinanceType.OutFlow),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DocumentPurchaseTaxInvoice:
      headerMessage = i18n.t("Success_DocumentPurchaseTaxInvoice");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.TaxInvoice, FinanceType.OutFlow),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DocumentWithholdingTax:
      headerMessage = i18n.t("Success_DocumentWithholdingTax");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.WithholdingTax, FinanceType.None),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.ImportTransactionPurchaseInventory:
      headerMessage = i18n.t("Success_ImportTransactionPurchaseInventory");
      descriptionMessage = i18n.t("Import_Code_And_Count_Transaction_Description", {
        importNumber: messageUrlName,
        x: transactionCount,
      });
      break;
    case NotificationMessageType.ImportTransactionExpense:
      headerMessage = i18n.t("Success_ImportTransactionExpense");
      descriptionMessage = i18n.t("Import_Code_And_Count_Transaction_Description", {
        importNumber: messageUrlName,
        x: transactionCount,
      });
      break;
    case NotificationMessageType.ImportTransactionInvoice:
      headerMessage = i18n.t("Success_ImportTransactionInvoice");
      descriptionMessage = i18n.t("Import_Code_And_Count_Transaction_Description", {
        importNumber: messageUrlName,
        x: transactionCount,
      });
      break;
    case NotificationMessageType.ImportTransactionReceipt:
      headerMessage = i18n.t("Success_ImportTransactionReceipt");
      descriptionMessage = i18n.t("Import_Code_And_Count_Transaction_Description", {
        importNumber: messageUrlName,
        x: transactionCount,
      });
      break;
    case NotificationMessageType.ImportTransactionQuotation:
      headerMessage = i18n.t("Success_ImportTransactionQuotation");
      descriptionMessage = i18n.t("Import_Code_And_Count_Transaction_Description", {
        importNumber: messageUrlName,
        x: transactionCount,
      });
      break;
    case NotificationMessageType.ImportTransactionPurchaseOrder:
      headerMessage = i18n.t("Success_ImportTransactionPurchaseOrder");
      descriptionMessage = i18n.t("Import_Code_And_Count_Transaction_Description", {
        importNumber: messageUrlName,
        x: transactionCount,
      });
      break;
    case NotificationMessageType.Statement:
      headerMessage = i18n.t("Success_Statement");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.AllReportWithHoldingTaxPND2:
      headerMessage = i18n.t("Success_AllReportWithHoldingTaxPND2");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.AllReportWithHoldingTaxPND3:
      headerMessage = i18n.t("Success_AllReportWithHoldingTaxPND3");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.AllReportWithHoldingTaxPND53:
      headerMessage = i18n.t("Success_AllReportWithHoldingTaxPND53");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.ChequeReport:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("ChequeReport"),
        successMessage: i18n.t("Success_ChequeReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.WorkingPaper:
      headerMessage = i18n.t("Success_WorkingPaperReport");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.RecalCostOfGoodSoldByMerchantCalculated:
      headerMessage = i18n.t("Success_RecalCostOfGoodSoldByMerchantCalculated");
      break;
    case NotificationMessageType.RecalCostOfGoodSoldByProductCalculated:
      headerMessage = i18n.t("Success_RecalCostOfGoodSoldByProductCalculated");
      descriptionMessage = i18n.t("Product_Code_Description", {
        productCode: GetProductCodeByProductId(productId, productCodeListForMap),
      });
      break;
    case NotificationMessageType.RecalCostOfGoodSoldFailed:
      headerMessage = i18n.t("Success_RecalCostOfGoodSoldFailed");
      descriptionMessage = i18n.t("Product_Code_Description", {
        productCode: GetProductCodeByProductId(productId, productCodeListForMap),
      });
      break;
    case NotificationMessageType.DebitNote:
      headerMessage = i18n.t("Success_DebitNoteReport");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.DebitNoteExpense:
      headerMessage = i18n.t("Success_DebitNoteExpenseReport");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.DocumentDebitNote:
      headerMessage = i18n.t("Success_DocumentDebitNote");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.DebitNote, FinanceType.InFlow),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DocumentDebitNoteExpense:
      headerMessage = i18n.t("Success_DocumentDebitNoteExpense");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.DebitNote, FinanceType.OutFlow),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DocumentPurchaseRequisition:
      headerMessage = i18n.t("Success_DocumentPurchaseRequisition");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.PurchaseRequisition, FinanceType.OutFlow),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DocumentProductRequisition:
      headerMessage = i18n.t("Success_DocumentProductRequisition");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.ProductRequisition, FinanceType.None),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.VoidImportTransactionFailed:
      headerMessage = i18n.t("Success_VoidImportTransactionFailed");
      descriptionMessage = i18n.t("Import_Code_Description", {
        importNumber: messageUrlName,
      });
      break;
    case NotificationMessageType.VoidImportTransactionSuccess:
      headerMessage = i18n.t("Success_VoidImportTransactionSuccess");
      descriptionMessage = i18n.t("Import_Code_Description", {
        importNumber: messageUrlName,
      });
      break;
    case NotificationMessageType.ImportTransactionReceiptFromInvoice:
      headerMessage = i18n.t("Success_ImportTransactionReceiptFromInvoice");
      descriptionMessage = i18n.t("Import_Code_And_Count_Transaction_Description", {
        importNumber: messageUrlName,
        x: transactionCount,
      });
      break;
    case NotificationMessageType.ImportTransactionJournal:
      headerMessage = i18n.t("Success_ImportTransactionJournal");
      descriptionMessage = i18n.t("Import_Code_And_Count_Transaction_Description", {
        importNumber: messageUrlName,
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DepreciateAssetMonthly:
      headerMessage = i18n.t("Success_DepreciateAssetMonthly") + " " + messageDescription;
      break;
    case NotificationMessageType.TaxPnd:
      headerMessage = i18n.t("Success_WithHoldingTaxReport");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.TaxPP30SaleTax:
      headerMessage = i18n.t("Success_TaxInvoiceReport");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.TaxPP30PurchaseTax:
      headerMessage = i18n.t("Success_PurchaseTaxInvoiceReport");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.TaxFetchPP30Transaction:
      headerMessage = i18n.t("Success_TaxFetchPP30Transaction");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.TaxFetchWhtCertificate:
      headerMessage = i18n.t("Success_TaxFetchWhtCertificate");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.FinanceBankStatement:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("Statement"),
        successMessage: i18n.t("Success_FinanceBankStatementReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.ExecutiveSummary:
      headerMessage = i18n.t("Success_ExecutiveSummaryReport");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.StatementOfCashflow:
      headerMessage = i18n.t("Success_StatementOfCashflow");
      descriptionMessage = i18n.t("Export_Period_Description", {
        fromDate: exportFromDate,
        toDate: exportToDate,
      });
      break;
    case NotificationMessageType.MentionTransaction:
      headerMessage = i18n.t("Success_MentionTransaction", {
        userName: userName,
        transactionNumber: GetTransactionNumberFromTransactionTypeListForMap(
          transactionId,
          transactionType,
          transactionTypeListForMap,
          journalViewDetailListForMap
        ),
      });
      descriptionMessage = GetCommentTextForDisplay(messageDescription, userIdListForMap);
      break;
    case NotificationMessageType.Deposit:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("DepositReport"),
        successMessage: i18n.t("Success_DepositReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.ReceiveDeposit:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("ReceiveDepositReport"),
        successMessage: i18n.t("Success_ReceiveDepositReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.InvoiceReceipt:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("InvoiceReceiptReport"),
        successMessage: i18n.t("Success_InvoiceReceiptReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.GoodsReceipt:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("GoodsReceiptReport"),
        successMessage: i18n.t("Success_GoodsReceiptReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.ApprovedTransaction:
      headerMessage = i18n.t("Success_ApprovedTransaction", {
        userName: userName,
      });
      descriptionMessage = i18n.t("Success_ApprovedTransaction_Description", {
        transactionType: GetTransactionNameForNotification(transactionType, financeType, transactionItemType),
        transactionNumber: GetTransactionNumberFromTransactionTypeListForMap(
          transactionId,
          transactionType,
          transactionTypeListForMap,
          journalViewDetailListForMap
        ),
      });
      break;
    case NotificationMessageType.SendApprovedDocumentTransaction:
      headerMessage = i18n.t("Success_SendApprovedTransaction", {
        userName: userName,
      });
      descriptionMessage = i18n.t("Success_SendApprovedTransaction_Description", {
        transactionType: GetTransactionNameForNotification(transactionType, financeType, transactionItemType),
        transactionNumber: GetTransactionNumberFromTransactionTypeListForMap(
          transactionId,
          transactionType,
          transactionTypeListForMap,
          journalViewDetailListForMap
        ),
      });
      break;
    case NotificationMessageType.EmailAttachTooLarge:
      headerMessage = i18n.t("Success_EmailAttachTooLarge");
      descriptionMessage = "";
      break;
    case NotificationMessageType.RemovePeakSupport:
      headerMessage = i18n.t("Success_RemovePeakSupport");
      descriptionMessage = i18n.t("Success_RemovePeakSupport_Description");
      break;
    case NotificationMessageType.RecieveBenefit:
    case NotificationMessageType.CamPaignBeforeExpire:
    case NotificationMessageType.BeforeExpireByDate:
    case NotificationMessageType.QuotaExpire:
      headerMessage = messageDescription;
      break;
    case NotificationMessageType.PaymentTransfer:
      headerMessage = i18n.t("Download_Bulk_Payment_Transfer");
      break;
    case NotificationMessageType.DocumentDepositReceipt:
      headerMessage = i18n.t("Success_DocumentDepositReceipt");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.Deposit, FinanceType.InFlow),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DocumentDepositPayment:
      headerMessage = i18n.t("Success_DocumentDepositPayment");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.Deposit, FinanceType.OutFlow),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DocumentInvoiceReceipt:
      headerMessage = i18n.t("Success_InvoiceReceipt");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.InvoiceReceipt, FinanceType.OutFlow),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.DocumentGoodsReceipt:
      headerMessage = i18n.t("Success_GoodsReceipt");
      descriptionMessage = i18n.t("Download_Transaction_Description", {
        transactionType: GetTransactionNameForNotification(TransactionType.GoodsReceipt, FinanceType.OutFlow),
        x: transactionCount,
      });
      break;
    case NotificationMessageType.BankAccountChanged:
      headerMessage = i18n.t("Bank_Account_Changed", {
        financeCode: GetBankAccountCodeByBankAccountId(bankAccountId, bankAccountListForMap),
      });
      descriptionMessage = i18n.t("Bank_Account_Changed_Description", {
        username: userName,
        date: notificationDateTime.GetDateFormat(),
        time: notificationTime,
      });
      break;
    case NotificationMessageType.PaymentChannelChanged:
      headerMessage = i18n.t("Payment_Channel_Changed");
      descriptionMessage = i18n.t("Payment_Channel_Changed_Description", {
        username: userName,
        date: notificationDateTime.GetDateFormat(),
        time: notificationTime,
      });
      break;
    case NotificationMessageType.BayStatementSuccess:
      headerMessage = i18n.t("Success_BayStatement_Header");
      descriptionMessage = i18n.t("Success_BayStatement_Description");
      break;
    case NotificationMessageType.AddUserToUserRole:
      headerMessage = i18n.t("Add_User_To_User_Role", {
        email: remark,
        userRole: roleName,
      });
      descriptionMessage = i18n.t("Add_User_To_User_Role_Description", {
        displayName: userName,
        date: notificationDateTime.GetDateFormat(),
        time: notificationTime,
      });
      break;
    case NotificationMessageType.CreateOrEditUserRole:
      headerMessage = i18n.t("Create_Or_Edit_User_Role", {
        userRole: roleName,
      });
      descriptionMessage = i18n.t("Create_Or_Edit_User_Role_Description", {
        displayName: userName,
        date: notificationDateTime.GetDateFormat(),
        time: notificationTime,
      });
      break;
    case NotificationMessageType.UpdateUserInRole:
      headerMessage = i18n.t("Update_User_In_Role", {
        email: remark,
      });
      descriptionMessage = i18n.t("Update_User_In_Role_Description", {
        displayName: userName,
        date: notificationDateTime.GetDateFormat(),
        time: notificationTime,
      });
      break;
    case NotificationMessageType.IFSInvoice:
      headerMessage = i18n.t("IFSInvoice_Header");
      descriptionMessage = i18n.t("IFSInvoice_Description");
      break;
    case NotificationMessageType.UpdateMerchantSettingFeature:
      const featureSettings = remark ? JSON.parse(remark) : null;
      const isActiveOldPEAK = featureSettings?.feature?.isActiveOldPEAK;

      if (isActiveOldPEAK) {
        headerMessage = i18n.t("Old_PEAK_Enabled_Header");
        descriptionMessage = i18n.t("Old_PEAK_Enabled_Description");
      } else {
        headerMessage = i18n.t("Old_PEAK_Disabled_Header");
        descriptionMessage = i18n.t("Old_PEAK_Disabled_Description");
      }
      break;
    case NotificationMessageType.FinanceReconcile:
      ({ headerMessage, descriptionMessage } = getNotificationHeaderAndDescriptionMessage({
        reportName: i18n.t("BankReconcileReport"),
        successMessage: i18n.t("Success_BankReconcileReport"),
        notificationEventType: notificationEventType,
        transactionCount: transactionCount,
        exportFromDate: exportFromDate,
        exportToDate: exportToDate,
        receiveEmailList: receiveEmailList,
        descriptionMessageOnSuccess: getExportPeriodDescription(exportFromDate, exportToDate),
      }));
      break;
    case NotificationMessageType.EditDocumentTransaction:
    case NotificationMessageType.AttachFileDocumentTransaction:
    case NotificationMessageType.RemoveFileDocumentTransaction:
    case NotificationMessageType.PaidPaymentDocumentTransaction:
    case NotificationMessageType.VoidDocumentTransaction:
    case NotificationMessageType.VoidPaymentDocumentTransaction:
    case NotificationMessageType.EditPaymentDocumentTransaction:
    case NotificationMessageType.AcceptDocumentTransaction:
    case NotificationMessageType.GotReceiptDocumentTransaction:
    case NotificationMessageType.VoidGotReceiptDocumentTransaction:
    case NotificationMessageType.EditRemarkDocumentTransaction:
    case NotificationMessageType.EditDueDateDocumentTransaction:
    case NotificationMessageType.EditReferenceNameDocumentTransaction:
    case NotificationMessageType.EditProductDocumentTransaction:
    case NotificationMessageType.EditNumberDocumentTransaction:
    case NotificationMessageType.EditContactDocumentTransaction:
    case NotificationMessageType.EditPaymentDateDocumentTransaction:
    case NotificationMessageType.EditIssueDateDocumentTransaction:
    case NotificationMessageType.EditWithholdingTaxDocumentTransaction:
    case NotificationMessageType.EditClassificationTaxDocumentTransaction:
    case NotificationMessageType.BackToAwaitAcceptedQuotation:
    case NotificationMessageType.UpdateStatusCompletely:
    case NotificationMessageType.UndoUpdateStatusCompletely:
    case NotificationMessageType.CancelTaskPurchaseRequisition:
    case NotificationMessageType.AcceptTaskPurchaseRequisition:
    case NotificationMessageType.NotApprovedDocumentTransaction:
    case NotificationMessageType.ChangeToDraftDocumentTransaction:
    case NotificationMessageType.RegisterTaxInvoice:
    case NotificationMessageType.RejectTransaction:
    case NotificationMessageType.CommentDocumentTransaction:
    case NotificationMessageType.SendForApproveDocumentTransaction:
    case NotificationMessageType.ApprovedDocumentTransaction:
      const transactionNumber = GetTransactionNumberFromTransactionTypeListForMap(
        transactionId,
        transactionType,
        transactionTypeListForMap,
        journalViewDetailListForMap,
      );
      const action = GetDocumentNotificationActionName(messageTypeId);

      headerMessage = i18n.t("Document_Notification.Document_Notification_Title", {
        userName,
        action,
        transactionNumber,
      });
      descriptionMessage = i18n.t("Document_Notification.Document_Notification_Body", {
        transactionType: GetTransactionNameForNotification(transactionType, financeType, transactionItemType),
        transactionNumber,
      });
      break;
  }
  return { headerMessage, descriptionMessage };
};

export const ConvertToNotificationMessageForDisplayModel = (notification: NotificationMessageModel) => {
  const notificationMessageForDisplay: NotificationMessageForDisplayModel = new NotificationMessageForDisplayModel();
  notificationMessageForDisplay.id = notification.id;
  notificationMessageForDisplay.merchantId = notification.merchantId;
  notificationMessageForDisplay.userId = notification.userId;
  notificationMessageForDisplay.notificationTypeId = notification.notificationTypeId;
  notificationMessageForDisplay.messageTypeId = notification.messageTypeId;
  notificationMessageForDisplay.notificationDateTime = notification.notificationDateTime;
  notificationMessageForDisplay.option = notification.option;
  notificationMessageForDisplay.isSeen = notification.isSeen;
  return notificationMessageForDisplay;
};

export const ConvertNotificationMessageListToNotificationMessageForDisplayList = (
  getUnSeenNotificationList: Array<NotificationMessageModel>
) => {
  const allNotificationList: Array<NotificationMessageForDisplayModel> = [];
  if (getUnSeenNotificationList && getUnSeenNotificationList.length > 0) {
    for (const getUnSeenNotification of getUnSeenNotificationList) {
      allNotificationList.push(ConvertToNotificationMessageForDisplayModel(getUnSeenNotification));
    }
  }
  return allNotificationList;
};

export const GetStatusIconNameAndColorByType = (
  messageTypeId: NotificationMessageType,
  notificationOption: NotificationMessageOptionModel
) => {
  switch (messageTypeId) {
    case NotificationMessageType.ImportTransactionPurchaseInventory:
    case NotificationMessageType.ImportTransactionExpense:
    case NotificationMessageType.ImportTransactionInvoice:
    case NotificationMessageType.ImportTransactionReceipt:
    case NotificationMessageType.ImportTransactionQuotation:
    case NotificationMessageType.ImportTransactionPurchaseOrder:
    case NotificationMessageType.ImportTransactionReceiptFromInvoice:
    case NotificationMessageType.ImportTransactionJournal:
      return {
        name: "fas fa-file-upload",
        color: "#43BF76",
      };
    case NotificationMessageType.VoidImportTransactionFailed:
    case NotificationMessageType.EmailAttachTooLarge:
      return {
        name: "icon-file_error",
        color: "#F1502F",
      };
    case NotificationMessageType.MentionTransaction:
      return {
        name: "icon-mention",
        color: "#5CACFF",
      };
    case NotificationMessageType.RemovePeakSupport:
      return {
        name: "fas fa-user-slash",
        color: "#FF6547",
      };
    case NotificationMessageType.ApprovedTransaction:
      return {
        name: "icon-file_success",
        color: "#43BF76",
      };
    case NotificationMessageType.SendApprovedDocumentTransaction:
      return {
        name: "fas fa-clock",
        color: "#ff9e40",
      };
    case NotificationMessageType.RecieveBenefit:
    case NotificationMessageType.CamPaignBeforeExpire:
    case NotificationMessageType.BeforeExpireByDate:
    case NotificationMessageType.QuotaExpire:
    case NotificationMessageType.RecalCostOfGoodSoldByMerchantCalculated:
    case NotificationMessageType.RecalCostOfGoodSoldByProductCalculated:
    case NotificationMessageType.RecalCostOfGoodSoldFailed:
    case NotificationMessageType.VoidImportTransactionSuccess:
    case NotificationMessageType.DepreciateAssetMonthly:
    case NotificationMessageType.BankAccountChanged:
    case NotificationMessageType.PaymentChannelChanged:
    case NotificationMessageType.BayStatementSuccess:
    case NotificationMessageType.AddUserToUserRole:
    case NotificationMessageType.CreateOrEditUserRole:
    case NotificationMessageType.UpdateUserInRole:
    case NotificationMessageType.UpdateMerchantSettingFeature:
      return {
        name: "fas fa-check-circle",
        color: "#43BF76",
      };
    case NotificationMessageType.IFSInvoice:
      return {
        name: "fa-solid fa-gift",
        color: "#6C6DF2",
      };
    case NotificationMessageType.DocumentQuotation:
    case NotificationMessageType.DocumentInvoice:
    case NotificationMessageType.DocumentReceipt:
    case NotificationMessageType.DocumentTaxInvoice:
    case NotificationMessageType.DocumentPurchaseOrder:
    case NotificationMessageType.DocumentExpense:
    case NotificationMessageType.DocumentBillingNote:
    case NotificationMessageType.DocumentBillingNoteExpense:
    case NotificationMessageType.DocumentCreditNote:
    case NotificationMessageType.DocumentCreditNoteExpense:
    case NotificationMessageType.DocumentPurchaseTaxInvoice:
    case NotificationMessageType.DocumentWithholdingTax:
    case NotificationMessageType.DocumentDebitNote:
    case NotificationMessageType.DocumentDebitNoteExpense:
    case NotificationMessageType.DailyJournal:
    case NotificationMessageType.DocumentDepositReceipt:
    case NotificationMessageType.DocumentDepositPayment:
    case NotificationMessageType.DocumentPurchaseRequisition:
    case NotificationMessageType.DocumentInvoiceReceipt:
    case NotificationMessageType.DocumentGoodsReceipt:
    case NotificationMessageType.DocumentProductRequisition:
      return {
        name: "fas fa-file-pdf",
        color: "#F1502F",
      };
    case NotificationMessageType.ReportDocument:
    case NotificationMessageType.StatementEFiling:
      return {
        name: "fas fa-file-alt",
        color: "#6c6df2",
      };
    case NotificationMessageType.PP30TaxFilingTextFile:
    case NotificationMessageType.WithHoldingTaxPND2:
    case NotificationMessageType.WithHoldingTaxPND3:
    case NotificationMessageType.WithHoldingTaxPND53:
    case NotificationMessageType.PaymentTransfer:
      return {
        name: "icon-file_text",
        color: "#3297FF",
      };
    case NotificationMessageType.AgingReportAP:
    case NotificationMessageType.AgingReportAR:
    case NotificationMessageType.AllGeneralLedger:
    case NotificationMessageType.Asset:
    case NotificationMessageType.AssetTemplateGroup:
    case NotificationMessageType.AssetTemplate:
    case NotificationMessageType.BalanceSheet:
    case NotificationMessageType.BillingNoteInvoice:
    case NotificationMessageType.BillingNoteInvoiceAllInvoice:
    case NotificationMessageType.ChartOfAccount:
    case NotificationMessageType.BillingNoteExpense:
    case NotificationMessageType.BillingNoteExpenseAllExpense:
    case NotificationMessageType.CreditNoteExpense:
    case NotificationMessageType.CreditNote:
    case NotificationMessageType.Journal:
    case NotificationMessageType.Expense:
    case NotificationMessageType.ExpenseAllProduct:
    case NotificationMessageType.Finance:
    case NotificationMessageType.GeneralLedgerTransaction:
    case NotificationMessageType.Invoice:
    case NotificationMessageType.InvoiceAllProduct:
    case NotificationMessageType.PurcahseOrder:
    case NotificationMessageType.PurchaseOrderAsset:
    case NotificationMessageType.PurchaseRequisition:
    case NotificationMessageType.PP30InputVat:
    case NotificationMessageType.PP30OutputVat:
    case NotificationMessageType.Product:
    case NotificationMessageType.ProfitLoss:
    case NotificationMessageType.PurchaseAsset:
    case NotificationMessageType.PurchaseAssetAllProduct:
    case NotificationMessageType.PurcahseTaxInvoice:
    case NotificationMessageType.PurchaseTaxInvoiceInRevenueDepartFormat:
    case NotificationMessageType.Quotation:
    case NotificationMessageType.Receipt:
    case NotificationMessageType.StockActivityStockCardMovement:
    case NotificationMessageType.TaxPP30SaleTax:
    case NotificationMessageType.TrialBalanceWithSubAccount:
    case NotificationMessageType.Unit:
    case NotificationMessageType.WithHeldTax:
    case NotificationMessageType.WithHoldingTax:
    case NotificationMessageType.Contact:
    case NotificationMessageType.PurchaseTaxInvoiceNotUse:
    case NotificationMessageType.SalesTaxInvoiceNotUse:
    case NotificationMessageType.CashflowStatementReport:
    case NotificationMessageType.AllReportSalesByCustomerReport:
    case NotificationMessageType.AllReportSalesByAccountReport:
    case NotificationMessageType.AllReportProductSalesByContact:
    case NotificationMessageType.AllReport_ProductServiceSalesSummaryReportOnDailyAccount:
    case NotificationMessageType.AllReport_RemainingFinanceBalanceReport:
    case NotificationMessageType.Pnd53TaxFilingTextFile:
    case NotificationMessageType.Pnd3TaxFilingTextFile:
    case NotificationMessageType.Statement:
    case NotificationMessageType.AllReportWithHoldingTaxPND2:
    case NotificationMessageType.AllReportWithHoldingTaxPND3:
    case NotificationMessageType.AllReportWithHoldingTaxPND53:
    case NotificationMessageType.ChequeReport:
    case NotificationMessageType.WorkingPaper:
    case NotificationMessageType.TrialBalanceSeperateMonth:
    case NotificationMessageType.TaxFetchPP30Transaction:
    case NotificationMessageType.TaxFetchWhtCertificate:
    case NotificationMessageType.AllReportPurchaseTaxInvoiceFromOldPeak:
    case NotificationMessageType.AllReportPurchaseTaxInvoiceFromPeakTax:
    case NotificationMessageType.AllReportTaxInvoiceFromOldPeak:
    case NotificationMessageType.AllReportTaxInvoiceFromPeakTax:
    case NotificationMessageType.AllReportPurchaseTaxInvoiceNotUseFromOldPeak:
    case NotificationMessageType.AllReportSalesTaxInvoiceNotUseFromOldPeak:
    case NotificationMessageType.AllReportPurchaseTaxInvoiceNotUseFromPeakTax:
    case NotificationMessageType.AllReportSalesTaxInvoiceNotUseFromPeakTax:
    case NotificationMessageType.FinanceBankStatement:
    case NotificationMessageType.ExecutiveSummary:
    case NotificationMessageType.StatementOfCashflow:
    case NotificationMessageType.StatementEFilingExcel:
    case NotificationMessageType.DebitNote:
    case NotificationMessageType.DebitNoteExpense:
    case NotificationMessageType.TaxInvoice:
    case NotificationMessageType.TaxInvoiceInRevenueDepartmentFormat:
    case NotificationMessageType.TaxPnd:
    case NotificationMessageType.TaxPP30PurchaseTax:
    case NotificationMessageType.TrialBalance:
    case NotificationMessageType.ReceiveDeposit:
    case NotificationMessageType.Deposit:
    case NotificationMessageType.InvoiceReceipt:
    case NotificationMessageType.GoodsReceipt:
    case NotificationMessageType.ProductRequisition:
    case NotificationMessageType.FinanceReconcile:
      if (notificationOption && notificationOption.notificationEventType) {
        if (notificationOption.notificationEventType === NotificationEventType.UnexpectedErrorWhileCreatingReport) {
          return {
            name: "icon-file_error",
            color: "#F1502F",
          };
        }
        if (notificationOption.notificationEventType === NotificationEventType.ExcelReportRowLimitExceed) {
          return {
            name: "icon-file_error",
            color: "#F1502F",
          };
        }
      }
      return {
        name: "fas fa-file-excel",
        color: "#2E8552",
      };

    default:
      return {
        name: "",
        color: "",
      };
  }
  // icon for send approve
  // name: "fas fa-clock",
  // color: "#FF9E40",
};

export const GetNotificationUrlImageSystem = (messageTypeId: NotificationMessageType) => {
  switch (messageTypeId) {
    case NotificationMessageType.IFSInvoice:
      return (
        process.env.VUE_APP_PeakFileServer?.toString().UrlCombine(
          "/peakengine/Content/Image/MerchantPartnerQualifiedLead/IFSNotificationIcon.png"
        ) ?? ""
      );
    default:
      return (
        process.env.VUE_APP_PeakFileServer?.toString().UrlCombine(
          "/peakengine/Content/Image/Notification/notification_icon_system.png"
        ) ?? ""
      );
  }
};

export const GetAllDataForDisplayNotification = async (
  objectId: ObjectIdListNotification,
  notificationList: Array<NotificationMessageForDisplayModel>,
  merchant: SelectedMerchantModel
) => {
  //#region Get User Detail
  await store.dispatch("Notification/DoGetUserByUserId", objectId.userIdList, { root: true });
  //#endregion

  //#region Get Transaction Code
  await store.dispatch(
    "Notification/DoGetTransactionViewDetailForDisplay",
    {
      transactionIdList: objectId.productRequisitionIdList,
      transactionType: TransactionType.ProductRequisition,
    },
    { root: true }
  );

  await store.dispatch(
    "Notification/DoGetTransactionViewDetailForDisplay",
    {
      transactionIdList: objectId.purchaseRequisitionIdList,
      transactionType: TransactionType.PurchaseRequisition,
    },
    { root: true }
  );

  await store.dispatch(
    "Notification/DoGetTransactionViewDetailForDisplay",
    {
      transactionIdList: objectId.quotationIdList,
      transactionType: TransactionType.Quotation,
    },
    { root: true }
  );

  await store.dispatch(
    "Notification/DoGetTransactionViewDetailForDisplay",
    {
      transactionIdList: objectId.depositIdList,
      transactionType: TransactionType.Deposit,
    },
    { root: true }
  );

  await store.dispatch(
    "Notification/DoGetTransactionViewDetailForDisplay",
    {
      transactionIdList: objectId.goodsReceiptIdList,
      transactionType: TransactionType.GoodsReceipt,
    },
    { root: true }
  );

  await store.dispatch(
    "Notification/DoGetTransactionViewDetailForDisplay",
    {
      transactionIdList: objectId.invoiceReceiptIdList,
      transactionType: TransactionType.InvoiceReceipt,
    },
    { root: true }
  );

  await store.dispatch(
    "Notification/DoGetTransactionViewDetailForDisplay",
    {
      transactionIdList: objectId.invoiceIdList,
      transactionType: TransactionType.Invoice,
    },
    { root: true }
  );

  await store.dispatch(
    "Notification/DoGetTransactionViewDetailForDisplay",
    {
      transactionIdList: objectId.receiptIdList,
      transactionType: TransactionType.Receipt,
    },
    { root: true }
  );

  await store.dispatch(
    "Notification/DoGetTransactionViewDetailForDisplay",
    {
      transactionIdList: objectId.taxInvoiceIdList,
      transactionType: TransactionType.TaxInvoice,
    },
    { root: true }
  );

  await store.dispatch(
    "Notification/DoGetTransactionViewDetailForDisplay",
    {
      transactionIdList: objectId.creditNoteIdList,
      transactionType: TransactionType.CreditNote,
    },
    { root: true }
  );

  await store.dispatch(
    "Notification/DoGetTransactionViewDetailForDisplay",
    {
      transactionIdList: objectId.debitNoteIdList,
      transactionType: TransactionType.DebitNote,
    },
    { root: true }
  );

  await store.dispatch(
    "Notification/DoGetTransactionViewDetailForDisplay",
    {
      transactionIdList: objectId.billingNoteIdList,
      transactionType: TransactionType.BillingNote,
    },
    { root: true }
  );
  //#endregion

  //#region Get Journal Code
  await store.dispatch("Notification/DoGetJournalViewDetailForDisplay", {
    journalIdList: objectId.journalIdList,
  });
  //#endregion

  //#region Get Product Code
  await store.dispatch("Notification/DoGetProductCodeForDisplay", objectId.productIdList, { root: true });
  //#endregion

  //#region Get assetTemplate Code
  await store.dispatch("Notification/DoGetAssetTemplateCodeForDisplay", objectId.assetTemplateIdList, { root: true });
  //#endregion

  //#region Get Bank Account Code
  await store.dispatch("Notification/DoGetBankAccountCodeForDisplay", objectId.bankAccountIdList, { root: true });
  //#endregion

  const userIdListForMap = store.getters["Notification/getUserIdListForMap"];
  const transactionTypeListForMap = store.getters["Notification/getTransactionTypeListForMap"];
  const journalViewDetailListForMap = store.getters["Notification/getJournalViewDetailListForMap"];
  const productCodeListForMap = store.getters["Notification/getProductCodeListForMap"];
  const assetTemplateCodeListForMap = store.getters["Notification/getAssetTemplateCodeListForMap"];
  const bankAccountListForMap = store.getters["Notification/getBankAccountListForMap"];

  if (notificationList && notificationList.length > 0) {
    notificationList.forEach((notification) => {
      notification.icon = GetStatusIconNameAndColorByType(notification.messageTypeId, notification.option);

      notification.message = GetSuccessHeaderReport(
        notification.messageTypeId,
        notification.option,
        notification.notificationDateTime,
        merchant,
        userIdListForMap,
        transactionTypeListForMap,
        journalViewDetailListForMap,
        productCodeListForMap,
        assetTemplateCodeListForMap,
        bankAccountListForMap
      );

      if (notification.option && notification.option.referenceUserId > 0) {
        notification.notificationUrlImage = GetUserImageByUserId(notification.option.referenceUserId, userIdListForMap);
        const referenceUser = userIdListForMap.get(notification.option.referenceUserId);
        if (referenceUser) {
          notification.notificationUserName = referenceUser.displayName;
          notification.notificationUserId = referenceUser.userId;
        } else {
          notification.notificationUserName = "";
          notification.notificationUserId = notification.userId;
        }
      } else if (
        notification.option.notificationEventType &&
        notification.option.notificationEventType === NotificationEventType.ReportHasBeenSentToEmail
      ) {
        notification.notificationUrlImage =
          process.env.VUE_APP_PeakFileServer?.UrlCombine(
            "/peakengine/Content/Image/Notification/notification_icon_email_sent.png"
          ) ?? "";
      } else if (notification.notificationTypeId == NotificationType.System) {
        notification.notificationUrlImage = GetNotificationUrlImageSystem(notification.messageTypeId);
      } else {
        notification.notificationUrlImage =
          process.env.VUE_APP_PeakFileServer?.toString().UrlCombine(
            "/peakengine/Content/Image/Notification/notification_icon_download.png"
          ) ?? "";
      }
    });
  }
};

export const CheckNotificationIsNotChangeURL = (
  messageTypeId: NotificationMessageType,
  notificationEventType: NotificationEventType
) => {
  if (notificationEventType !== null) {
    const notificationEventTypeNoAction = new Set([
      NotificationEventType.UnexpectedErrorWhileCreatingReport,
      NotificationEventType.ExcelReportRowLimitExceed,
      NotificationEventType.ReportHasBeenSentToEmail,
    ]);
    return notificationEventTypeNoAction.has(notificationEventType);
  }
  const notificationIsNotChangeURLMap = new Set([
    NotificationMessageType.DepreciateAssetMonthly,
    NotificationMessageType.RemovePeakSupport,
    NotificationMessageType.EmailAttachTooLarge,
    NotificationMessageType.BankAccountChanged,
    NotificationMessageType.PaymentChannelChanged,
    NotificationMessageType.BayStatementSuccess,
    NotificationMessageType.UpdateMerchantSettingFeature,
  ]);
  return notificationIsNotChangeURLMap.has(messageTypeId);
};

export const GetDocumentNotificationActionName = (messageTypeId: NotificationMessageType) => {
  switch (messageTypeId) {
    case NotificationMessageType.EditDocumentTransaction:
      return i18n.t("Document_Notification.Edit_Document_Transaction_Action");
    case NotificationMessageType.AttachFileDocumentTransaction:
      return i18n.t("Document_Notification.Attach_File_Document_Transaction_Action");
    case NotificationMessageType.RemoveFileDocumentTransaction:
      return i18n.t("Document_Notification.Remove_File_Document_Transaction_Action");
    case NotificationMessageType.PaidPaymentDocumentTransaction:
      return i18n.t("Document_Notification.Paid_Payment_Document_Transaction_Action");
    case NotificationMessageType.VoidDocumentTransaction:
      return i18n.t("Document_Notification.Void_Document_Transaction_Action");
    case NotificationMessageType.VoidPaymentDocumentTransaction:
      return i18n.t("Document_Notification.Void_Payment_Document_Transaction_Action");
    case NotificationMessageType.EditPaymentDocumentTransaction:
      return i18n.t("Document_Notification.Edit_Payment_Document_Transaction_Action");
    case NotificationMessageType.AcceptDocumentTransaction:
      return i18n.t("Document_Notification.Accept_Document_Transaction_Action");
    case NotificationMessageType.GotReceiptDocumentTransaction:
      return i18n.t("Document_Notification.Got_Receipt_Document_Transaction_Action");
    case NotificationMessageType.VoidGotReceiptDocumentTransaction:
      return i18n.t("Document_Notification.Void_Got_Receipt_Document_Transaction_Action");
    case NotificationMessageType.EditRemarkDocumentTransaction:
      return i18n.t("Document_Notification.Edit_Remark_Document_Transaction_Action");
    case NotificationMessageType.EditDueDateDocumentTransaction:
      return i18n.t("Document_Notification.Edit_Due_Date_Document_Transaction_Action");
    case NotificationMessageType.EditReferenceNameDocumentTransaction:
      return i18n.t("Document_Notification.Edit_Reference_Name_Document_Transaction_Action");
    case NotificationMessageType.EditProductDocumentTransaction:
      return i18n.t("Document_Notification.Edit_Product_Document_Transaction_Action");
    case NotificationMessageType.EditNumberDocumentTransaction:
      return i18n.t("Document_Notification.Edit_Number_Document_Transaction_Action");
    case NotificationMessageType.EditContactDocumentTransaction:
      return i18n.t("Document_Notification.Edit_Contact_Document_Transaction_Action");
    case NotificationMessageType.EditPaymentDateDocumentTransaction:
      return i18n.t("Document_Notification.Edit_Payment_Date_Document_Transaction_Action");
    case NotificationMessageType.EditIssueDateDocumentTransaction:
      return i18n.t("Document_Notification.Edit_Issue_Date_Document_Transaction_Action");
    case NotificationMessageType.EditWithholdingTaxDocumentTransaction:
      return i18n.t("Document_Notification.Edit_Withholding_Tax_Document_Transaction_Action");
    case NotificationMessageType.EditClassificationTaxDocumentTransaction:
      return i18n.t("Document_Notification.Edit_Classification_Tax_Document_Transaction_Action");
    case NotificationMessageType.BackToAwaitAcceptedQuotation:
      return i18n.t("Document_Notification.Back_To_Await_Accepted_Quotation_Action");
    case NotificationMessageType.UpdateStatusCompletely:
      return i18n.t("Document_Notification.Update_Status_Completely_Action");
    case NotificationMessageType.UndoUpdateStatusCompletely:
      return i18n.t("Document_Notification.Undo_Update_Status_Completely_Action");
    case NotificationMessageType.CancelTaskPurchaseRequisition:
      return i18n.t("Document_Notification.Cancel_Task_Purchase_Requisition_Action");
    case NotificationMessageType.AcceptTaskPurchaseRequisition:
      return i18n.t("Document_Notification.Accept_Task_Purchase_Requisition_Action");
    case NotificationMessageType.NotApprovedDocumentTransaction:
      return i18n.t("Document_Notification.Not_Approved_Document_Transaction_Action");
    case NotificationMessageType.ChangeToDraftDocumentTransaction:
      return i18n.t("Document_Notification.Change_To_Draft_Document_Transaction_Action");
    case NotificationMessageType.RegisterTaxInvoice:
      return i18n.t("Document_Notification.Register_Tax_Invoice_Action");
    case NotificationMessageType.RejectTransaction:
      return i18n.t("Document_Notification.Reject_Transaction_Action");
    case NotificationMessageType.CommentDocumentTransaction:
      return i18n.t("Document_Notification.Comment_Document_Transaction_Action");
    case NotificationMessageType.SendForApproveDocumentTransaction:
      return i18n.t("Document_Notification.Send_For_Approve_Document_Transaction_Action");
    case NotificationMessageType.ApprovedDocumentTransaction:
      return i18n.t("Document_Notification.Approved_Document_Transaction_Action");
    case NotificationMessageType.SuccessfulPurchaseRequisition:
      return i18n.t("Document_Notification.Successful_Purchase_Requisition_Action");
    case NotificationMessageType.CancelSuccessfulPurchaseRequisition:
      return i18n.t("Document_Notification.Cancel_Successful_Purchase_Requisition_Action");
    default:
      return "";
  }
};

export const GetIsDocumentNotification = (messageTypeId: NotificationMessageType) => {
  switch (messageTypeId) {
    case NotificationMessageType.EditDocumentTransaction:
    case NotificationMessageType.AttachFileDocumentTransaction:
    case NotificationMessageType.RemoveFileDocumentTransaction:
    case NotificationMessageType.PaidPaymentDocumentTransaction:
    case NotificationMessageType.VoidDocumentTransaction:
    case NotificationMessageType.VoidPaymentDocumentTransaction:
    case NotificationMessageType.EditPaymentDocumentTransaction:
    case NotificationMessageType.AcceptDocumentTransaction:
    case NotificationMessageType.GotReceiptDocumentTransaction:
    case NotificationMessageType.VoidGotReceiptDocumentTransaction:
    case NotificationMessageType.EditRemarkDocumentTransaction:
    case NotificationMessageType.EditDueDateDocumentTransaction:
    case NotificationMessageType.EditReferenceNameDocumentTransaction:
    case NotificationMessageType.EditProductDocumentTransaction:
    case NotificationMessageType.EditNumberDocumentTransaction:
    case NotificationMessageType.EditContactDocumentTransaction:
    case NotificationMessageType.EditPaymentDateDocumentTransaction:
    case NotificationMessageType.EditIssueDateDocumentTransaction:
    case NotificationMessageType.EditWithholdingTaxDocumentTransaction:
    case NotificationMessageType.EditClassificationTaxDocumentTransaction:
    case NotificationMessageType.BackToAwaitAcceptedQuotation:
    case NotificationMessageType.UpdateStatusCompletely:
    case NotificationMessageType.UndoUpdateStatusCompletely:
    case NotificationMessageType.CancelTaskPurchaseRequisition:
    case NotificationMessageType.AcceptTaskPurchaseRequisition:
    case NotificationMessageType.NotApprovedDocumentTransaction:
    case NotificationMessageType.ChangeToDraftDocumentTransaction:
    case NotificationMessageType.RegisterTaxInvoice:
    case NotificationMessageType.RejectTransaction:
    case NotificationMessageType.CommentDocumentTransaction:
    case NotificationMessageType.SendForApproveDocumentTransaction:
    case NotificationMessageType.ApprovedDocumentTransaction:
    case NotificationMessageType.SuccessfulPurchaseRequisition:
    case NotificationMessageType.CancelSuccessfulPurchaseRequisition:
      return true;
    default:
      return false;
  }
}